/**
 * Nexus Auth (Authentication Service) Configuration
 * Centralized SSO Authentication
 *
 * All authentication is handled by the central Nexus Auth service
 * This service redirects to Nexus Auth for login/register and receives callbacks
 */

export const NEXUS_AUTH_CONFIG = {
  // Nexus Auth API base URL (supports both new and legacy env var names)
  API_URL: import.meta.env.VITE_NEXUS_AUTH_API_URL || import.meta.env.VITE_UMS_API_URL || 'http://auth.azyntorlabs.local:1201',

  // Nexus Auth Frontend URL (for SSO redirects)
  WEB_URL: import.meta.env.VITE_NEXUS_AUTH_WEB_URL || import.meta.env.VITE_UMS_WEB_URL || 'http://auth.azyntorlabs.local:1200',

  // Service identifier (used for branding and role scoping)
  SERVICE_NAME: import.meta.env.VITE_SERVICE_NAME || 'exam',

  // API endpoints (for reference - Nexus SDK handles these)
  ENDPOINTS: {
    SESSION: '/api/v1/auth/session',
    LOGOUT: '/api/v1/auth/logout',
    REFRESH: '/api/v1/auth/refresh',
    PROFILE: '/api/v1/user/profile',
  },

  /**
   * Get SSO login URL with service branding
   * Redirects user to centralized Nexus Auth login page
   */
  getLoginUrl: (returnUrl?: string) => {
    const webUrl = NEXUS_AUTH_CONFIG.WEB_URL;
    const service = NEXUS_AUTH_CONFIG.SERVICE_NAME;
    const redirect = returnUrl || window.location.href;
    return `${webUrl}/login?service=${service}&tenant=cybershield&redirect=${encodeURIComponent(redirect)}`;
  },

  /**
   * Get SSO register URL with service branding
   * Redirects user to centralized Nexus Auth register page
   */
  getRegisterUrl: (returnUrl?: string) => {
    const webUrl = NEXUS_AUTH_CONFIG.WEB_URL;
    const service = NEXUS_AUTH_CONFIG.SERVICE_NAME;
    const redirect = returnUrl || window.location.href;
    return `${webUrl}/register?service=${service}&tenant=cybershield&redirect=${encodeURIComponent(redirect)}`;
  },

  /**
   * Get SSO logout URL
   * Logs out from Nexus Auth and redirects back
   */
  getLogoutUrl: (returnUrl?: string) => {
    const webUrl = NEXUS_AUTH_CONFIG.WEB_URL;
    const redirect = returnUrl || window.location.origin;
    return `${webUrl}/logout?redirect=${encodeURIComponent(redirect)}`;
  },

  /**
   * Get forgot password URL
   * Redirects to centralized Nexus Auth forgot password page
   */
  getForgotPasswordUrl: (returnUrl?: string) => {
    const webUrl = NEXUS_AUTH_CONFIG.WEB_URL;
    const service = NEXUS_AUTH_CONFIG.SERVICE_NAME;
    const redirect = returnUrl || window.location.origin + '/auth/callback';
    return `${webUrl}/forgot-password?service=${service}&tenant=cybershield&redirect=${encodeURIComponent(redirect)}`;
  },

  /**
   * Redirect to SSO login
   */
  redirectToLogin: (returnUrl?: string) => {
    window.location.href = NEXUS_AUTH_CONFIG.getLoginUrl(returnUrl);
  },

  /**
   * Redirect to SSO register
   */
  redirectToRegister: (returnUrl?: string) => {
    window.location.href = NEXUS_AUTH_CONFIG.getRegisterUrl(returnUrl);
  },

  /**
   * Redirect to SSO logout
   */
  redirectToLogout: (returnUrl?: string) => {
    window.location.href = NEXUS_AUTH_CONFIG.getLogoutUrl(returnUrl);
  },
} as const;

/** @deprecated Use NEXUS_AUTH_CONFIG instead */
export const UMS_CONFIG = NEXUS_AUTH_CONFIG;

export default NEXUS_AUTH_CONFIG;
